import { FC, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { B2C, NotificationBanner } from 'cosmos-components';

import { UkBrandNames } from 'src/enums';
import { sciBrandNameSelector } from 'src/components/shared/SciPage/SciPage.selector';
import { NotificationBannerMessage } from 'src/components/shared/NotificationBannerMessage';
import globalMsg from 'src/messages';

import * as SShared from '../shared/products.form.style';

import { useVehicleProtectionsForm } from './vehicleProtections.hooks';
import * as S from './vehicleProtections.form.style';
import { MODULE_PREFIX } from './vehicleProtections.constants';
import msg from './messages';

export const VehicleProtectionsForm: FC = () => {
    const {
        isLoading,
        insurances,
        upgrade,
        upgradeSelected,
        control,
        formErrors,
        onSubmit,
    } = useVehicleProtectionsForm();
    const intl = useIntl();
    const brandName = useSelector(sciBrandNameSelector);
    const [visible, setVisible] = useState(false);
    const [popupInfo, setPopupInfo] = useState('');

    if (isLoading) {
        return <B2C.LoaderSpinner size="large" />;
    }
    if (!insurances || insurances.length <= 0) {
        return <div>No result</div>;
    }

    const isOneInsuranceOrUpgradeLoading =
        !!insurances.find((i) => i.isLoading) || upgrade?.isLoading;

    const hideLiabilityOnChildren = Object.values(UkBrandNames).includes(
        brandName as UkBrandNames,
    );

    return (
        <SShared.StyledForm onSubmit={onSubmit}>
            <Controller
                control={control}
                name="insurance"
                render={({ field }) => {
                    return (
                        <div>
                            {insurances.map((insurance) => {
                                const selected =
                                    insurance.id.productId === field.value;

                                return (
                                    <S.FormInsuranceContainer
                                        key={`${MODULE_PREFIX}-insurance-${insurance.id.productId}`}
                                    >
                                        <B2C.ProductCard
                                            type="radio"
                                            description={insurance.description}
                                            price={insurance.price}
                                            title={insurance.name}
                                            loading={insurance.isLoading}
                                            learnMoreAction={() => {
                                                setPopupInfo(
                                                    insurance.popupInformation,
                                                );
                                                setVisible(true);
                                            }}
                                            prepaid={insurance.prepaid}
                                            included={insurance.included}
                                            input={{
                                                ...field,
                                                onChange: field.onChange,
                                                value: insurance.id.productId,
                                                checked: selected,
                                                disabled:
                                                    !insurance.isLoading &&
                                                    isOneInsuranceOrUpgradeLoading,
                                            }}
                                            liability={
                                                !!upgradeSelected && !!upgrade
                                                    ? undefined
                                                    : insurance.liability
                                            }
                                        />
                                        {selected && upgrade && (
                                            <S.FormUpgradeContainer>
                                                <Controller
                                                    control={control}
                                                    name="upgrade"
                                                    render={({
                                                        field: upgradeField,
                                                    }) => (
                                                        <B2C.ProductCard
                                                            type="checkbox"
                                                            description={
                                                                upgrade.description
                                                            }
                                                            price={
                                                                upgrade.price
                                                            }
                                                            title={upgrade.name}
                                                            learnMoreAction={() => {
                                                                setPopupInfo(
                                                                    upgrade.popupInformation,
                                                                );
                                                                setVisible(
                                                                    true,
                                                                );
                                                            }}
                                                            loading={
                                                                upgrade.isLoading
                                                            }
                                                            prepaid={
                                                                upgrade.prepaid
                                                            }
                                                            included={
                                                                upgrade.included
                                                            }
                                                            input={{
                                                                ...upgradeField,
                                                                onChange:
                                                                    upgradeField.onChange,
                                                                value: !!upgradeField.value,
                                                                checked:
                                                                    !!upgradeField.value,
                                                                disabled:
                                                                    (!upgrade.isLoading &&
                                                                        isOneInsuranceOrUpgradeLoading) ||
                                                                    upgrade.readOnly,
                                                            }}
                                                            liability={
                                                                hideLiabilityOnChildren
                                                                    ? undefined
                                                                    : upgrade.liability
                                                            }
                                                        />
                                                    )}
                                                />
                                            </S.FormUpgradeContainer>
                                        )}
                                    </S.FormInsuranceContainer>
                                );
                            })}
                        </div>
                    );
                }}
            />
            <SShared.ActionWrapper>
                <SShared.StyledPrimaryButton
                    data-test-id={`${MODULE_PREFIX}-save`}
                    type="submit"
                >
                    {intl.formatMessage(globalMsg.buttons.continue)}
                </SShared.StyledPrimaryButton>
            </SShared.ActionWrapper>
            {!!formErrors?.insurance && (
                <NotificationBanner
                    message={
                        <NotificationBannerMessage
                            header={
                                <strong>
                                    {intl.formatMessage(
                                        msg.formHeaderItems.errorHeader,
                                    )}
                                </strong>
                            }
                            content={
                                <>
                                    {!!formErrors?.insurance && (
                                        <div>
                                            {formErrors.insurance.message}
                                        </div>
                                    )}
                                </>
                            }
                        />
                    }
                    type={'error'}
                    isClosable={false}
                    notificationIconSize="medium1"
                    hasBorder
                    isNotificationIconFilled
                />
            )}
            <S.StyledModal
                data-test-id="modal"
                visible={visible}
                onClose={() => setVisible(false)}
                closable={false}
                style={{
                    display: 'grid',
                    alignContent: 'center',
                    height: 'calc(100% - 6.4rem)',
                    margin: '0px auto',
                    marginTop: '3.2rem',
                    maxWidth: 'calc(100% - 6.4rem)',
                }}
            >
                <div dangerouslySetInnerHTML={{ __html: popupInfo }} />
                <S.StyledModalPrimaryButton onClick={() => setVisible(false)}>
                    <S.StyledExitIcon name="close" size="medium" isInverted />
                    <S.Close>
                        {intl.formatMessage(globalMsg.buttons.close)}
                    </S.Close>
                </S.StyledModalPrimaryButton>
            </S.StyledModal>
        </SShared.StyledForm>
    );
};
